import React, {
    useCallback,
    useRef,
    FunctionComponent,
    ChangeEvent,
    FocusEvent,
    KeyboardEvent,
    FormEvent,
} from 'react';
import classnames from 'classnames';
import { SIZES } from '../Common/constants';

// components
import { ClearButton } from './ClearButton';
import { Input } from '../Input';
import { RightSearchButton } from './RightSearchButton';
import { LeftSearchButton } from './LeftSearchButton';

// styles
import styles from './main.scss';

const { small, medium, large } = SIZES;

type PropsType = {
    onChange: (value: string) => void;
    dataTn: string;
    value: string;
    size: typeof small | typeof medium | typeof large;
    autoComplete?: boolean | string;
    disabled?: boolean;
    focusOnClear?: boolean;
    hasRightSearchButton?: boolean;
    isClearButtonHidden?: boolean;
    noBorder?: boolean;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    onClear?: () => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
    placeholder?: string;
};

const noop = (): void => {};

export const SearchBar: FunctionComponent<PropsType> = props => {
    const {
        autoComplete = false,
        disabled = false,
        focusOnClear = false,
        hasRightSearchButton = true,
        noBorder = false,
        onBlur = noop,
        onClear = noop,
        onFocus = noop,
        onKeyDown = noop,
        dataTn,
        isClearButtonHidden,
        onChange,
        onSubmit,
        placeholder,
        size,
        value,
    } = props;

    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
        onChange(e.target.value);
    };

    const handleOnBlur = useCallback(
        e => {
            inputRef.current = e.target;
            if (onBlur) {
                onBlur(e);
            }
        },
        [onBlur, inputRef]
    );

    const handleOnClear = useCallback(() => {
        if (
            inputRef &&
            inputRef.current &&
            typeof inputRef.current.focus === 'function' &&
            focusOnClear
        ) {
            inputRef.current.focus();
        }
        if (onChange) {
            onChange('');
        }

        if (onClear) {
            onClear();
        }
    }, [onChange, onClear, inputRef, focusOnClear]);

    const baseInputProps = {
        autoComplete,
        autoCorrect: false,
        dataTn,
        disabled,
        noBorder,
        onBlur: handleOnBlur,
        onChange: handleOnChange,
        onFocus,
        onKeyDown,
        placeholder,
        size,
        spellCheck: false,
        value,
    };

    const hideClearButton =
        typeof isClearButtonHidden !== 'undefined' ? isClearButtonHidden : !value;

    const clearButtonProps = {
        dataTn: `${dataTn}-clear`,
        size,
        onClear: handleOnClear,
        disabled,
        isHidden: hideClearButton,
    };

    const searchButtonProps = {
        dataTn: `${dataTn}-search-button`,
        disabled,
        hideClearButton,
        size,
    };

    const leftSearchButtonProps = {
        ...baseInputProps,
        leftDecorator: <LeftSearchButton {...searchButtonProps} />,
        leftDecoratorClass: styles.decoratorWrapper,
        rightDecorator: <ClearButton {...clearButtonProps} />,
        rightDecoratorClass: styles.decoratorWrapper,
    };

    const rightSearchButtonProps = {
        ...baseInputProps,
        rightDecorator: (
            <React.Fragment>
                <ClearButton {...clearButtonProps} />
                <RightSearchButton {...searchButtonProps} />
            </React.Fragment>
        ),
        rightDecoratorClass: classnames(styles.decoratorWrapper, {
            [styles.shiftRight]: !noBorder,
        }),
    };

    const inputProps = hasRightSearchButton ? rightSearchButtonProps : leftSearchButtonProps;

    if (onSubmit) {
        return (
            <form data-tn={`${dataTn}-form`} onSubmit={onSubmit}>
                <div className={styles.wrapper}>
                    <Input {...inputProps} />
                </div>
            </form>
        );
    } else {
        return (
            <div className={styles.wrapper}>
                <Input {...inputProps} />
            </div>
        );
    }
};
